import React from "react";
import { MultiModeListProps, MetricCardType, getValueColor } from "./cardTypes";
import { LIST_VIEW_CONFIGS, ListColumnDef, STATUS_CONFIG } from "../constants";

interface ProgramItem {
  program: string;
  parent_program: string | null;
  cm: string;
  cm_location: string;
}

// Find a program item from the list by matching program name, cm, and cm_location
const findStageItem = (programs: any[], program: string, cm: string, cmLocation: string) =>
  programs.find((p: any) => p.program === program && p.cm === cm && p.cm_location === cmLocation);

// Extract the numeric value for a column from a program item, converting API decimal to percentage (returns null if no data)
const getValue = (p: any, col: ListColumnDef): number | null => {
  const raw = col.stageKey ? (p?.stages?.[col.stageKey]?.[col.valueField] ?? null) : (p?.[col.valueField] ?? null);
  if (raw == null || raw === "") return null;
  const num = Number(raw);
  return isNaN(num) ? null : num * 100;
};

// Get the status string (e.g. "at_risk", "on_track") for a column from a program item
const getStatus = (p: any, col: ListColumnDef): string | undefined => {
  if (col.stageKey) return p?.stages?.[col.stageKey]?.status;
  return p?.status;
};

// Map a status string to its configured display color, with optional per-card overrides
const getStatusColor = (status: string | undefined, statusColorOverride?: Record<string, string>): string | undefined => {
  if (!status) return undefined;
  if (statusColorOverride?.[status]) return statusColorOverride[status];
  const cfg = STATUS_CONFIG[status];
  return cfg ? cfg.color : undefined;
};

// Render a single sub-program row's metric values across all columns (used in expanded view)
const renderValues = (p: any, columns: ListColumnDef[], statusColorOverride?: Record<string, string>) => (
  <span className="multi-mode-values">
    {columns.map((col) => {
      const val = getValue(p, col);
      const status = getStatus(p, col);
      const color = val != null ? (getStatusColor(status, statusColorOverride) || getValueColor(val, col.target, col.warningOffset ?? 0, col.invert)) : "#d5dbdb";
      return (
        <span key={col.label} style={{ width: col.width, textAlign: "left", fontWeight: 700, color }}>
          {val != null ? `${val.toFixed(2)}%` : "—"}
        </span>
      );
    })}
  </span>
);

// Render parent program's values; fallback to first subprogram if parent lacks a stage; average across multiple cm+location combos
const renderParentValues = (subprograms: ProgramItem[], programs: any[], columns: ListColumnDef[], statusColorOverride?: Record<string, string>) => {
  const parentItems = subprograms.filter((s) => s.parent_program == null);
  const childItems = subprograms.filter((s) => s.parent_program != null);

  const results = columns.map((col) => {
    // Collect values from parent items (may have multiple cm+location combos)
    const parentVals: number[] = [];
    const statuses: string[] = [];
    parentItems.forEach((item) => {
      const p = findStageItem(programs, item.program, item.cm, item.cm_location);
      const hasStage = col.stageKey ? p?.stages?.[col.stageKey] != null : p != null;
      if (hasStage) {
        const v = getValue(p, col);
        if (v != null) parentVals.push(v);
        const s = getStatus(p, col);
        if (s) statuses.push(s);
      }
    });

    if (parentVals.length > 0) {
      const avg = parentVals.reduce((a, b) => a + b, 0) / parentVals.length;
      const worstStatus = statuses.includes("at_risk") ? "at_risk" : statuses.includes("below_target") ? "below_target" : statuses.length > 0 ? statuses[0] : undefined;
      return { val: avg, status: worstStatus };
    }

    // Fallback: parent doesn't have this stage, find the first subprogram that has it and average across its cm+location combos
    const firstChildWithStage = childItems.find((child) => {
      const p = findStageItem(programs, child.program, child.cm, child.cm_location);
      return col.stageKey ? p?.stages?.[col.stageKey] != null : p != null;
    });

    if (firstChildWithStage) {
      const sameNameItems = childItems.filter((c) => c.program === firstChildWithStage.program);
      const childVals: number[] = [];
      const childStatuses: string[] = [];
      sameNameItems.forEach((child) => {
        const p = findStageItem(programs, child.program, child.cm, child.cm_location);
        const hasStage = col.stageKey ? p?.stages?.[col.stageKey] != null : p != null;
        if (hasStage) {
          const v = getValue(p, col);
          if (v != null) childVals.push(v);
          const s = getStatus(p, col);
          if (s) childStatuses.push(s);
        }
      });

      if (childVals.length > 0) {
        const avg = childVals.reduce((a, b) => a + b, 0) / childVals.length;
        const worstStatus = childStatuses.includes("at_risk") ? "at_risk" : childStatuses.includes("below_target") ? "below_target" : childStatuses.length > 0 ? childStatuses[0] : undefined;
        return { val: avg, status: worstStatus };
      }
    }

    return { val: null as number | null, status: undefined as string | undefined };
  });

  return (
    <span className="multi-mode-values">
      {columns.map((col, i) => {
        const { val, status } = results[i];
        const color = val != null ? (getStatusColor(status, statusColorOverride) || getValueColor(val, col.target, col.warningOffset ?? 0, col.invert)) : "#d5dbdb";
        return (
          <span key={col.label} style={{ width: col.width, textAlign: "left", fontWeight: 700, color }}>
            {val != null ? `${val.toFixed(2)}%` : "—"}
          </span>
        );
      })}
    </span>
  );
};

// Multi-program list view: displays programs grouped by parent, with expandable sub-programs grouped by CM & Location
export const MultiModeList: React.FC<MultiModeListProps> = ({ cardId, metricsData, isProgramExpanded, toggleProgramExpand }) => {
  const listConfig = LIST_VIEW_CONFIGS[cardId as Exclude<MetricCardType, "phic_alert_status">];
  if (!listConfig) return null;

  const { dataKey, columns, statusColorOverride } = listConfig;
  const allItems = (metricsData as any)[dataKey]?.programs ?? [];

  // Group by program (parent_program or program itself)
  const programGroups = new Map<string, ProgramItem[]>();
  allItems.forEach((item: ProgramItem) => {
    const programName = item.parent_program || item.program;
    if (!programGroups.has(programName)) programGroups.set(programName, []);
    programGroups.get(programName)!.push(item);
  });

  const programEntries = Array.from(programGroups.entries());

  return (
    <div data-testid={`multi-mode-list-${cardId}`} className="card-program-list multi-mode-list">
      {/* Column headers */}
      <div data-testid="multi-headers" className="multi-mode-header">
        {columns.map((col) => (
          <span key={col.label} data-testid={`multi-header-${col.label.toLowerCase().replace(/\s+/g, "-")}`} style={{ width: col.width, textAlign: "left" }}>{col.label}</span>
        ))}
      </div>
      {/* Program rows */}
      {programEntries.map(([programName, subprograms], pIdx) => {
        const hasSubprograms = subprograms.length > 1;
        const isExpanded = isProgramExpanded(cardId, programName);
        const isLast = pIdx === programEntries.length - 1;

        // Group subprograms by cm_location
        const cmLocationGroups: Record<string, ProgramItem[]> = {};
        subprograms.forEach((sub) => {
          const locationKey = `${sub.cm} - ${sub.cm_location}`;
          if (!cmLocationGroups[locationKey]) cmLocationGroups[locationKey] = [];
          cmLocationGroups[locationKey].push(sub);
        });

        return (
          <div key={programName} className={isLast ? "multi-mode-program-group-last" : "multi-mode-program-group"} data-testid={`multi-program-row-${programName}`} data-program-expanded={String(isExpanded)}>
            {/* Program name row with expand/collapse toggle */}
            <div className={`multi-mode-program-row ${isExpanded ? "multi-mode-program-row-expanded" : ""}`}>
              <div
                data-testid={`multi-program-row-toggle-${programName}`}
                onClick={hasSubprograms ? (e) => { e.stopPropagation(); e.preventDefault(); toggleProgramExpand(cardId, programName); } : undefined}
                className="multi-mode-expand-trigger"
                style={{ cursor: hasSubprograms ? "pointer" : "default" }}
              >
                <span className="multi-mode-expand-icon" data-testid={`multi-program-row-triangle-${programName}`}>
                  {hasSubprograms && (
                    <span className="multi-mode-expand-arrow">
                      {isExpanded ? "▼" : "▶"}
                    </span>
                  )}
                </span>
                <span className="multi-mode-program-name" data-testid={`multi-program-row-name-${programName}`}>
                  {programName}
                  {hasSubprograms && !isExpanded && (
                    <span className="multi-mode-program-count" data-testid={`multi-program-row-plus-${programName}`}>({subprograms.length - 1})</span>
                  )}
                </span>
              </div>
              {/* Parent program values shown when collapsed */}
              {!isExpanded && (
                <div className="multi-mode-values-container" data-testid={`multi-program-row-value-${programName}`}>
                  {renderParentValues(subprograms, allItems, columns, statusColorOverride)}
                </div>
              )}
            </div>
            {/* Expanded: sub-program rows grouped by CM & Location */}
            {isExpanded && Object.entries(cmLocationGroups).map(([cmLocation, subItems]) => (
              <div key={cmLocation} className="multi-mode-cm-group" data-testid={`multi-cm-group-${programName}-${cmLocation}`}>
                <div className="multi-mode-cm-label" data-testid={`multi-cm-label-${programName}-${cmLocation}`}>{cmLocation}</div>
                {subItems.map((subprogram, sIdx) => (
                  <div key={`${subprogram.program}-${sIdx}`} className="multi-mode-sub-row" data-testid={`multi-subprogram-row-${programName}-${subprogram.program}`}>
                    <span title={subprogram.program} className={`multi-mode-sub-name ${subprogram.program === programName ? "multi-mode-sub-name-parent" : "multi-mode-sub-name-child"}`}>{subprogram.program}</span>
                    <div className="multi-mode-values-container" data-testid={`multi-subprogram-row-value-${programName}-${subprogram.program}`}>
                      {renderValues(findStageItem(allItems, subprogram.program, subprogram.cm, subprogram.cm_location), columns, statusColorOverride)}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
